import { ComponentProps } from 'react';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';

import { OpSkeleton } from 'new-components/DLS/OpSkeleton/OpSkeleton';
import { OpTimezonePicker } from 'new-components/DLS/OpTimezonePicker/OpTimezonePicker';

import { useOpFormContext } from '../../OpFormContext';
import { OpForm } from '../../OpForm';

interface OpFormTimezonePickerProps
  extends Pick<
      ComponentProps<typeof OpForm.Item>,
      | 'name'
      | 'label'
      | 'rules'
      | 'tooltip'
      | 'validateTrigger'
      | 'validateStatus' // Temp: For SUIR wrapped version
      | 'help' // Temp: For SUIR wrapped version
      | 'required' // Temp: For SUIR wrapped version
    >,
    ComponentProps<typeof OpTimezonePicker> {
  formItemClassName?: string;
  isLoading?: boolean;
  includeDeviceTimezone?: boolean;
}

export const OpFormTimezonePicker = ({
  // Form Item props
  formItemClassName,
  name,
  label,
  tooltip,
  rules,
  validateTrigger,
  required, // TEMP: For old form component validation
  validateStatus, // TEMP: For old form component validation
  help, // TEMP: For old form component validation

  // For skeleton loading state
  isLoading,

  // Wrapped element props
  disabled,
  testId,
  includeDeviceTimezone = false,
  ...elementProps
}: OpFormTimezonePickerProps) => {
  /** The Form is wrapped in a provider that passes the loading context
   *  to the component */
  const { isDataLoading, isReadOnly } = useOpFormContext();

  let opFormItemTestId: string | undefined;
  if (testId) {
    opFormItemTestId = `${testId}-op-form-item`;
  } else if (typeof label === 'string') {
    opFormItemTestId = `${label}-op-form-item`;
  }
  const uuid = uuidv4();
  const opSelectId = `op-timezone-picker-${uuid}`;

  return (
    <OpForm.Item
      className={clsx(formItemClassName)}
      name={name}
      label={label}
      rules={rules}
      tooltip={tooltip}
      validateTrigger={validateTrigger}
      required={required} // TEMP: For old form component validation
      validateStatus={validateStatus} // TEMP: For old form component validation
      help={help} // TEMP: For old form component validation
      testId={opFormItemTestId}
      {...(!name && { htmlFor: opSelectId })} // If a name isn't passed there is no association between the label and the input. This creates that association.
    >
      {isDataLoading || isLoading ? (
        <OpSkeleton.Input active block />
      ) : (
        <OpTimezonePicker
          includeDeviceTimezone={includeDeviceTimezone}
          disabled={isReadOnly || disabled}
          {...(!name && { id: opSelectId })} // If a name isn't passed there is no association between the label and the input. This creates that association.
          {...elementProps}
          testId={testId}
        />
      )}
    </OpForm.Item>
  );
};
